import { CategoriaEnum } from '@acme/auth'
import Cookies from 'js-cookie'
import jwt_decode from 'jwt-decode'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
    FuncionarioUsuario,
    Login,
    Token,
    User,
    auth as auth_api,
} from 'service/api'
import tokenRefresher from 'service/api/token-refresher'
import { toast } from 'sonner'
import { useStore } from 'store'
import { rememberMeStore } from 'templates/Auth/store'
import { TokenDecoded } from 'types'
import { queryClient } from '../providers/ReactQuery'
import { SessaoInicial, resolverSessaoInicial } from './sessao-inicial'

interface AuthContextProps {
    auth: boolean
    loading: boolean
    /** Sessão sem JWT válido sendo renovada pelo refresh token ao abrir o app. */
    isRestaurandoSessao: boolean
    decoded: TokenDecoded
    handleLogout: () => void
    /** Resolve `false` quando o login falha — o erro já sai em toast. */
    handleLogin: (
        data: (Login | Token) & { navigateTo?: string },
    ) => Promise<boolean>
    user: User
    funcionarioSelecionado?: FuncionarioUsuario
    selecionarFuncionario: (funcionario: FuncionarioUsuario) => Promise<boolean>
}

type Props = {
    children: React.ReactNode
}

export const AuthContext = createContext({} as AuthContextProps)

function decodedDaSessao(sessao: SessaoInicial) {
    return sessao.estado === 'autenticada'
        ? sessao.decoded
        : ({} as TokenDecoded)
}

export const AuthContextProvider: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate()
    const { setEmail } = rememberMeStore()
    const [isLoading, setIsLoading] = useState(false)
    const { mutateAsync } = auth_api.login.mutations.useCreate()

    const tokenCookie = Cookies.get('JWT')
    const sessaoInicial = resolverSessaoInicial(
        tokenCookie,
        Cookies.get('refreshToken'),
    )

    const [auth, setAuth] = useState(sessaoInicial.estado === 'autenticada')
    const [isRestaurandoSessao, setIsRestaurandoSessao] = useState(
        sessaoInicial.estado === 'renovar',
    )
    const [loggedUser, setLoggedUser] = useState<User>({} as User)
    const funcionarioSelecionado = JSON.parse(
        String(sessionStorage.getItem('funcionario')),
    )
    const selecionarFuncionario = async (
        funcionario: FuncionarioUsuario | undefined | null,
    ) => {
        if (!funcionario) {
            sessionStorage.removeItem('funcionario')
            return true
        }

        // Sem id não há cliente a trocar; só libera o guard de rota.
        if (funcionario.id === undefined) {
            sessionStorage.setItem('funcionario', JSON.stringify(funcionario))
            return true
        }

        const token = await tokenRefresher.refreshToken(true, funcionario.id)
        if (!token) {
            toast.error('Não foi possível trocar de funcionário')
            return false
        }

        // Os dados em cache pertencem ao cliente do funcionário anterior.
        queryClient.removeQueries()
        setTokenDecode(jwt_decode<TokenDecoded>(token))
        sessionStorage.setItem('funcionario', JSON.stringify(funcionario))
        return true
    }

    const [tokenDecode, setTokenDecode] = useState(
        decodedDaSessao(sessaoInicial),
    )

    useEffect(() => {
        const sessao = resolverSessaoInicial(
            tokenCookie,
            Cookies.get('refreshToken'),
        )

        if (sessao.estado === 'autenticada') {
            setTokenDecode(sessao.decoded)
            setAuth(true)
            return
        }

        if (sessao.estado === 'anonima') {
            if (tokenCookie) tokenRefresher.logoff(false)
            setAuth(false)
            return
        }

        // Sem renovar aqui nenhuma query dispara, nenhum 401 chega ao
        // interceptor e a tela fica carregando para sempre.
        setIsRestaurandoSessao(true)
        tokenRefresher
            .refreshToken(false)
            .then(token => {
                // Falha no refresh já limpou os cookies; o guard de rota leva ao login.
                if (!token) return
                setTokenDecode(jwt_decode<TokenDecoded>(token))
                setAuth(true)
            })
            .catch(() => tokenRefresher.logoff(false))
            .finally(() => setIsRestaurandoSessao(false))
    }, [tokenCookie])

    const handleLogin = useCallback(
        async (
            data: (Login | Token) & { navigateTo?: string },
        ): Promise<boolean> => {
            setIsLoading(true)
            try {
                Cookies.remove('JWT')
                Cookies.remove('refreshToken')
                selecionarFuncionario(null)

                let accessToken: Token['token'],
                    refreshToken: Token['refreshToken'],
                    user: Token['user'],
                    rememberMe: Login['rememberMe']

                if ('email' in data) {
                    const response = await mutateAsync(data, {
                        onError: e => {
                            throw e
                        },
                    })
                    accessToken = response.token
                    refreshToken = response.refreshToken
                    user = response.user
                    rememberMe = data.rememberMe
                } else {
                    accessToken = data.token
                    refreshToken = data.refreshToken
                    user = data.user
                    rememberMe = false
                }
                setLoggedUser(user)

                useStore.persist.clearStorage()
                useStore.setState({
                    companyId: -1,
                    company: null,
                    employeeId: -1,
                    employee: null,
                    costCenterId: -1,
                    costCenter: null,
                    locationId: -1,
                    location: null,
                })

                Cookies.set('JWT', accessToken, { expires: 1, sameSite: 'lax' })
                if (refreshToken)
                    Cookies.set('refreshToken', refreshToken, {
                        expires: 15,
                        sameSite: 'lax',
                    })

                const decode = jwt_decode<TokenDecoded>(accessToken)

                rememberMeStore.setState({
                    emailAvaliacao: null,
                    openAvaliacao: null,
                })

                setAuth(true)

                rememberMe && setEmail(decode.email)
                setTokenDecode(decode)

                const len = user?.Funcionario?.length ?? 0
                if (data.navigateTo) {
                    if (decode?.categoria === CategoriaEnum.Funcionario)
                        await selecionarFuncionario(
                            user?.Funcionario?.find(
                                funcionario => !funcionario.dtRescisao,
                            ) ?? ({} as FuncionarioUsuario),
                        )
                    navigate(data.navigateTo)
                    return true
                } else if (
                    decode?.categoria === CategoriaEnum.Funcionario &&
                    len > 1
                )
                    navigate('/selecionar-funcionario')
                else if (decode?.categoria === CategoriaEnum.Funcionario)
                    await selecionarFuncionario(
                        user?.Funcionario?.find(
                            funcionario => !funcionario.dtRescisao,
                        ) ?? ({} as FuncionarioUsuario),
                    )
                else navigate('/dashboard')
                return true
            } catch (err: any) {
                toast.error(err.message ?? 'Erro ao efetuar login')
                return false
            } finally {
                setIsLoading(false)
            }
        },
        [setEmail, navigate],
    )

    async function handleLogout() {
        setAuth(false)
        queryClient.removeQueries()
        sessionStorage.clear()
        useStore.persist.clearStorage()
        tokenRefresher.logoff(false)
        return <Navigate to="/" />
    }

    const value: AuthContextProps = {
        auth,
        handleLogin,
        handleLogout,
        loading: isLoading,
        isRestaurandoSessao,
        decoded: tokenDecode,
        user: loggedUser,
        funcionarioSelecionado,
        selecionarFuncionario,
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

type InfoSuporte =
    | { isSuporte: false }
    | (Omit<TokenDecoded, 'cliente' | 'diasAvaliacao' | 'isAtivo'> & {
          isSuporte: true
          refreshToken: string
      })

export function getInfoSuporte(): InfoSuporte {
    const tokenSuporte = Cookies.get('tokenSuporte')
    if (!tokenSuporte) return { isSuporte: false }
    const decode = jwt_decode<TokenDecoded>(tokenSuporte)

    return {
        ...decode,
        refreshToken: tokenSuporte,
        isSuporte: true,
    }
}
